import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBranchContext } from "@/contexts/BranchContext";
import { useBranches } from "@/hooks/useBranches";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { clearPaywallPreviewCache } from "@/hooks/usePaywallPreview";
import { ANGEL_BUSINESS_ID } from "@/constants";
import { Plus, TrendingDown, Trash2 } from "lucide-react";
import { NoMatchingRecords } from "@/components/NoMatchingRecords";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getCurrentMonthInZambia, getTodayInZambia, formatMonthLabel, getZambiaDateKey } from "@/lib/zambia-time";
import { format } from "date-fns";
import { useEmployeeViewSettings } from "@/hooks/useEmployeeViewSettings";
import { RestrictedPageNotice } from "@/components/RestrictedPageNotice";
import { useInvalidateReorderQueries } from "@/hooks/useReorderIntelligence";

/** Must match DB constraint expenses_category_check — saved as category (text) to expenses table */
const EXPENSE_CATEGORIES = ["Marketing", "Restock", "Rent", "Utilities", "Salaries", "Logistics", "Tax", "Other"] as const;

interface Expense {
  id: string;
  category: string;
  description: string | null;
  amount: number;
  expense_date: string;
  date: string | null;
  created_by: string | null;
}

interface RestockInventoryItem {
  id: string;
  name: string;
  sku: string;
}

export default function ExpensesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, businessId, profile, isFounder, role, refreshProfile } = useAuth();
  const isReviewboostsServices = user?.email?.toLowerCase().trim() === "reviewboosts.services@gmail.com";
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [creatorMap, setCreatorMap] = useState<Record<string, { email?: string; full_name?: string }>>({});
  const isAdmin = role === "admin" || isFounder;
  const { selectedBranchId } = useBranchContext();
  const { branches } = useBranches();
  const [addOpen, setAddOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => getCurrentMonthInZambia());
  const [categoryFilter, setCategoryFilter] = useState<"all" | (typeof EXPENSE_CATEGORIES)[number]>("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (searchParams.get("openAdd") === "1") {
      setAddOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openAdd");
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(getTodayInZambia());
  const [restockItems, setRestockItems] = useState<RestockInventoryItem[]>([]);
  const [restockInventoryId, setRestockInventoryId] = useState("");
  const [restockQty, setRestockQty] = useState(1);
  const [restockUnitCost, setRestockUnitCost] = useState(0);
  const [resolvedBusinessId, setResolvedBusinessId] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const { toast } = useToast();
  const { settings: employeeViewSettings } = useEmployeeViewSettings();
  const invalidateReorderQueries = useInvalidateReorderQueries();

  if (role === "employee" && !employeeViewSettings.allowEmployeeRecordExpenses) {
    return (
      <RestrictedPageNotice
        title="Expenses are disabled"
        description="Your manager has turned off expense logging for employee accounts."
      />
    );
  }

  useEffect(() => {
    if (!user?.id || (businessId ?? profile?.business_id ?? resolvedBusinessId)) return;
    supabase.from("profiles").select("business_id").eq("user_id", user.id).single().then(({ data }) => {
      if (data?.business_id) setResolvedBusinessId(data.business_id);
    });
  }, [user?.id, businessId, profile?.business_id, resolvedBusinessId]);

  const fetchExpenses = async (overrideBusinessId?: string | null) => {
    const effectiveBusinessId = overrideBusinessId ?? businessId ?? profile?.business_id ?? resolvedBusinessId;
    if (!effectiveBusinessId) return;

    const [y, m] = selectedMonth.split("-").map(Number);
    const lastDay = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
    const startDate = `${selectedMonth}-01`;
    const endDate = `${selectedMonth}-${String(lastDay).padStart(2, "0")}`;

    let baseQuery = supabase
      .from("expenses")
      .select("id, category, description, amount, expense_date, date, created_by, branch_id")
      .gte("expense_date", startDate)
      .lte("expense_date", endDate)
      .order("expense_date", { ascending: false })
      .eq("business_id", effectiveBusinessId);
    if (!isAdmin) baseQuery = baseQuery.eq("created_by", user?.id ?? "");
    if (selectedBranchId !== "all") baseQuery = baseQuery.eq("branch_id", selectedBranchId);
    const { data } = await baseQuery;
    if (data) {
      setExpenses(data as Expense[]);
      const creatorIds = [...new Set((data as Expense[]).map((e) => e.created_by).filter(Boolean))] as string[];
      if (creatorIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, email, full_name")
          .in("user_id", creatorIds);
        const map: Record<string, { email?: string; full_name?: string }> = {};
        (profiles ?? []).forEach((p) => {
          map[p.user_id] = { email: p.email ?? undefined, full_name: p.full_name ?? undefined };
        });
        setCreatorMap(map);
      }
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [businessId, profile?.business_id, selectedBranchId, selectedMonth]);

  useEffect(() => {
    const fetchRestockItems = async () => {
      const effectiveBusinessId = businessId ?? profile?.business_id ?? resolvedBusinessId;
      if (!effectiveBusinessId) return;
      let q = supabase
        .from("inventory")
        .select("id, name, sku")
        .eq("business_id", effectiveBusinessId)
        .order("name");
      if (selectedBranchId !== "all") q = q.eq("branch_id", selectedBranchId);
      const { data } = await q;
      const rows = (data ?? []) as RestockInventoryItem[];
      setRestockItems(rows);
      if (!restockInventoryId && rows.length > 0) setRestockInventoryId(rows[0].id);
    };
    void fetchRestockItems();
  }, [businessId, profile?.business_id, resolvedBusinessId, selectedBranchId, restockInventoryId]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!category || !EXPENSE_CATEGORIES.includes(category as (typeof EXPENSE_CATEGORIES)[number])) {
      toast({ title: "Invalid category", description: "Please select a category from the list.", variant: "destructive" });
      return;
    }
    const isRestock = category === "Restock";
    if (isRestock && (!restockInventoryId || restockQty < 1 || restockUnitCost < 0)) {
      toast({
        title: "Invalid restock entry",
        description: "Select an item and enter valid quantity and unit cost.",
        variant: "destructive",
      });
      return;
    }
    const resolvedAmount = isRestock ? Number((restockQty * restockUnitCost).toFixed(2)) : (parseFloat(amount) || 0);
    let targetBusinessId = businessId ?? profile?.business_id ?? resolvedBusinessId ?? null;
    if (!targetBusinessId && user.id) {
      const { data } = await supabase.from("profiles").select("business_id").eq("user_id", user.id).single();
      targetBusinessId = data?.business_id ?? null;
      if (targetBusinessId) setResolvedBusinessId(targetBusinessId);
    }
    const hasWrongBusiness = isReviewboostsServices && (targetBusinessId === ANGEL_BUSINESS_ID);
    const needEnsure =
      isReviewboostsServices && !isFounder ||
      (!targetBusinessId && !isFounder && (role === "admin" || isReviewboostsServices)) ||
      (isReviewboostsServices && !isFounder && (!targetBusinessId || hasWrongBusiness));
    if (needEnsure) {
      const branchId = selectedBranchId !== "all" ? selectedBranchId : branches[0]?.id ?? null;
      if (isRestock) {
        const { data: result, error: rpcError } = await supabase.rpc("record_restock_expense", {
          p_inventory_id: restockInventoryId,
          p_quantity: restockQty,
          p_unit_cost: restockUnitCost,
          p_description: description?.trim() || "",
          p_expense_date: expenseDate,
          p_branch_id: branchId,
        });
        if (rpcError) {
          toast({ title: "Error", description: rpcError.message || "Could not record restock.", variant: "destructive" });
          return;
        }
        const res = result as { expense_id?: string; business_id?: string } | null;
        if (res?.business_id) {
          setResolvedBusinessId(res.business_id);
          await refreshProfile();
          clearPaywallPreviewCache();
          setSuccessBanner("Restock recorded and inventory updated");
          setTimeout(() => setSuccessBanner(null), 4000);
          invalidateReorderQueries();
          toast({ title: "Restock recorded" });
          setCategory("");
          setDescription("");
          setAmount("");
          setRestockQty(1);
          setRestockUnitCost(0);
          setAddOpen(false);
          fetchExpenses(res.business_id);
          setTimeout(() => fetchExpenses(res.business_id), 800);
        }
        return;
      }
      const { data: result, error: rpcError } = await supabase.rpc("insert_expense_for_self", {
        p_category: category,
        p_description: description?.trim() || "",
        p_amount: resolvedAmount,
        p_expense_date: expenseDate,
        p_branch_id: branchId,
      });
      if (rpcError) {
        toast({ title: "Error", description: rpcError.message || "Could not add expense.", variant: "destructive" });
        return;
      }
      const res = result as { id?: string; business_id?: string } | null;
      if (res?.id && res?.business_id) {
        const newExpense: Expense = {
          id: res.id,
          category,
          description: description?.trim() || null,
          amount: parseFloat(amount) || 0,
          expense_date: expenseDate,
          date: new Date().toISOString(),
          created_by: user.id,
        };
        setExpenses((prev) => [newExpense, ...prev.filter((e) => e.id !== res.id)]);
        setResolvedBusinessId(res.business_id);
        await refreshProfile();
        clearPaywallPreviewCache();
        setSuccessBanner("Expense added successfully");
        setTimeout(() => setSuccessBanner(null), 4000);
        toast({ title: "Expense Added" });
        setCategory("");
        setDescription("");
        setAmount("");
        setAddOpen(false);
        fetchExpenses(res.business_id);
        setTimeout(() => fetchExpenses(res.business_id), 800);
      }
      return;
    }
    if (!targetBusinessId) {
      const needsSetup = role === "admin" || isReviewboostsServices;
      toast({
        title: needsSetup ? "Setting up your account" : "No business",
        description: needsSetup
          ? "Click Refresh in the sidebar to load your business."
          : "Your account is not linked to a business.",
        variant: "destructive",
      });
      return;
    }
    const branchId = selectedBranchId !== "all" ? selectedBranchId : branches[0]?.id ?? null;
    if (isRestock) {
      const { data: result, error } = await supabase.rpc("record_restock_expense", {
        p_inventory_id: restockInventoryId,
        p_quantity: restockQty,
        p_unit_cost: restockUnitCost,
        p_description: description?.trim() || "",
        p_expense_date: expenseDate,
        p_branch_id: branchId,
      });
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }
      const res = result as { expense_id?: string; business_id?: string } | null;
      setSuccessBanner("Restock recorded and inventory updated");
      setTimeout(() => setSuccessBanner(null), 4000);
      toast({ title: "Restock recorded" });
      setCategory("");
      setDescription("");
      setAmount("");
      setRestockQty(1);
      setRestockUnitCost(0);
      setAddOpen(false);
      invalidateReorderQueries();
      if (res?.business_id) {
        fetchExpenses(res.business_id);
      } else {
        fetchExpenses();
      }
      return;
    }
    const { data: inserted, error } = await supabase
      .from("expenses")
      .insert({
        business_id: targetBusinessId,
        category,
        description: description?.trim() || "",
        amount: resolvedAmount,
        expense_date: expenseDate,
        date: new Date().toISOString(),
        created_by: user.id,
        branch_id: branchId,
      })
      .select("id, category, description, amount, expense_date, date, created_by")
      .single();
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSuccessBanner("Expense added successfully");
      setTimeout(() => setSuccessBanner(null), 4000);
      toast({ title: "Expense Added" });
      setCategory("");
      setDescription("");
      setAmount("");
      setAddOpen(false);
      if (inserted) {
        setExpenses((prev) => [inserted as Expense, ...prev]);
        const creatorIds = [user.id];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, email, full_name")
          .in("user_id", creatorIds);
        const map: Record<string, { email?: string; full_name?: string }> = {};
        (profiles ?? []).forEach((p) => {
          map[p.user_id] = { email: p.email ?? undefined, full_name: p.full_name ?? undefined };
        });
        setCreatorMap((prev) => ({ ...prev, ...map }));
      }
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("expenses").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchExpenses();
    }
  };

  const filteredExpenses = useMemo(() => {
    let list = expenses;
    if (categoryFilter !== "all") {
      list = list.filter((e) => e.category === categoryFilter);
    }
    return list;
  }, [expenses, categoryFilter]);

  const total = useMemo(
    () => filteredExpenses.reduce((s, e) => s + Number(e.amount), 0),
    [filteredExpenses]
  );

  const monthOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const today = getTodayInZambia();
    const [y, m] = today.split("-").map(Number);
    for (let i = 0; i < 24; i++) {
      const date = new Date(Date.UTC(y!, (m ?? 1) - 1 - i, 1));
      const ym = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
      options.push({ value: ym, label: formatMonthLabel(ym) });
    }
    return options;
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth, categoryFilter, expenses.length]);

  const ENTRIES_PER_PAGE = 20;
  const totalPages = Math.max(1, Math.ceil(filteredExpenses.length / ENTRIES_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = filteredExpenses.length === 0 ? 0 : (safePage - 1) * ENTRIES_PER_PAGE;
  const pageEnd = Math.min(pageStart + ENTRIES_PER_PAGE, filteredExpenses.length);

  const pageExpenses = useMemo(
    () => filteredExpenses.slice(pageStart, pageEnd),
    [filteredExpenses, pageStart, pageEnd]
  );

  const groupedPageExpenses = useMemo(() => {
    const groups: { dateKey: string; expenses: Expense[] }[] = [];
    let lastKey: string | null = null;
    for (const exp of pageExpenses) {
      const key = getZambiaDateKey(exp.expense_date);
      if (key !== lastKey) {
        groups.push({ dateKey: key, expenses: [] });
        lastKey = key;
      }
      groups[groups.length - 1].expenses.push(exp);
    }
    return groups;
  }, [pageExpenses]);

  return (
    <div className="space-y-6">
      {successBanner && (
        <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-700 dark:text-emerald-300">
          {successBanner}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">Expenses</h2>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? "Track business costs. View who logged each expense." : "Add and track your own business expense entries. Your manager will still see them."}
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={(open) => { setAddOpen(open); if (open && !category) setCategory("Other"); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0">
              <Plus className="h-4 w-4 mr-2" /> Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Expense</DialogTitle>
              {!isAdmin && (
                <p className="text-sm text-muted-foreground">Record a business expense. Your manager will see it.</p>
              )}
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <Label>Category</Label>
                {/* Category dropdown: required; saved to expenses.category */}
                <Select value={category || undefined} onValueChange={setCategory}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional details" /></div>
              {category === "Restock" && (
                <>
                  <div>
                    <Label>Item</Label>
                    <Select value={restockInventoryId || undefined} onValueChange={setRestockInventoryId}>
                      <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                      <SelectContent>
                        {restockItems.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.name} ({item.sku})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={restockQty}
                        onChange={(e) => setRestockQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                        required
                      />
                    </div>
                    <div>
                      <Label>Unit Cost (ZMW)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={restockUnitCost}
                        onChange={(e) => setRestockUnitCost(Math.max(0, Number(e.target.value) || 0))}
                        required
                      />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label>Amount (ZMW)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={category === "Restock" ? (restockQty * restockUnitCost).toFixed(2) : amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required={category !== "Restock"}
                  readOnly={category === "Restock"}
                />
              </div>
              <div><Label>Date</Label><Input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} required /></div>
              <Button type="submit" className="w-full bg-primary text-primary-foreground">Save Expense</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-critical" /> All Expenses
            </span>
            <span className="text-lg font-bold">Total: K{total.toLocaleString()}</span>
          </CardTitle>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Month</span>
              <Select value={selectedMonth} onValueChange={(v) => setSelectedMonth(v)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Category</span>
              <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as typeof categoryFilter)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {EXPENSE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-muted-foreground">
              {formatMonthLabel(selectedMonth)} · {filteredExpenses.length} expense
              {filteredExpenses.length === 1 ? "" : "s"}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {filteredExpenses.length === 0 ? (
            expenses.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">No expenses recorded yet.</p>
            ) : (
              <NoMatchingRecords />
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-semibold">Category</th>
                    <th className="text-left py-3 px-2 font-semibold">Description</th>
                    <th className="text-right py-3 px-2 font-semibold">Amount</th>
                    <th className="text-right py-3 px-2 font-semibold">Date</th>
                    {isAdmin && <th className="text-left py-3 px-2 font-semibold">Logged by</th>}
                    <th className="text-right py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedPageExpenses.map(({ dateKey, expenses: groupExpenses }) => (
                    <Fragment key={dateKey}>
                      <tr className="bg-muted/30 border-b border-muted">
                        <td
                          colSpan={isAdmin ? 6 : 5}
                          className="py-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {format(new Date(dateKey), "EEEE, dd MMM yyyy")}
                        </td>
                      </tr>
                      {groupExpenses.map((exp) => {
                        const creator = exp.created_by ? creatorMap[exp.created_by] : null;
                        const creatorLabel = creator
                          ? (creator.full_name?.trim() || creator.email || "Team member")
                          : exp.created_by === user?.id
                            ? "You"
                            : "—";
                        return (
                          <tr key={exp.id} className="border-b border-border/50">
                            <td className="py-3 px-2 font-medium">{exp.category}</td>
                            <td className="py-3 px-2 text-muted-foreground">{exp.description || "—"}</td>
                            <td className="py-3 px-2 text-right font-bold">
                              K{Number(exp.amount).toLocaleString()}
                            </td>
                            <td
                              className="py-3 px-2 text-right text-muted-foreground whitespace-nowrap"
                              title={exp.expense_date}
                            >
                              {exp.expense_date ? format(new Date(exp.expense_date), "dd MMM yyyy") : "—"}
                            </td>
                            {isAdmin && (
                              <td className="py-3 px-2 text-muted-foreground text-xs" title={creator?.email}>
                                {creatorLabel}
                              </td>
                            )}
                            <td className="py-3 px-2 text-right">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(exp.id)}
                                disabled={!isAdmin && exp.created_by !== user?.id}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-critical" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filteredExpenses.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                ← Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Showing {filteredExpenses.length === 0 ? 0 : pageStart + 1}–{pageEnd} of{" "}
                {filteredExpenses.length} expenses
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
